import { ProjectElementNode } from '@deltek/specification-client/core/Models/productListingDetail'
import { ProjectElementType } from '@deltek/specification-client/core/Models/projectElement'
import { TextContentElement } from '@deltek/specification-client/core/Models/textContentElement'
import clsx from 'clsx'
import { useShallowEditorActionBar } from 'components/Workspace2EditorActionBar/store/EditorActionBarStore'
import { processEnv } from 'components/configuration/environmentVariables'
import { PROPERTY_EDITOR_EDITABLE_TEXT_VARIANT as NODE_TYPES } from 'pages/ProjectsPage/WorkspacePage/components/constants'
import React, {
    ComponentType,
    ReactNode,
    useCallback,
    useMemo,
    useRef,
} from 'react'
import { useOMWorkspace } from 'shared/hooks/useOMWorkspace'
import { LRUCache } from 'shared/utils/lruCache'
import { useIsDragging } from '../DragAndDropStore'
import { useHoveredNode } from '../HoveredItemStore'
import { useShallowSelector } from '../Workspace2Store'
import ProjectProperties from '../components/ProjectProperties'
import { NodeTitleProps } from '../components/TreeView'
import WorkspaceTitle from '../components/WorkspaceTitle'
import { withLazyLoad } from '../components/withLazyComponent'
import { findNodeById } from '../features/promoteDemote/utils'
import { useTreeViewStyles } from '../styles'
import { ContentItemViewNodesType, DataNode } from '../types'
import { classifyNode, NodeClassification } from '../utils/bodUtils'
import { getImportTypeInfo } from '../utils/importTypeUtils'
import {
    isManufacturerOrBODHeader,
    isSyntheticButtonKey,
} from '../utils/manufacturerBodUtils'
import { getNodeOrderLabel } from '../utils/nodeOrderUtils'
import {
    filterUniqueNodes,
    getFilteredNodeArrays,
} from '../utils/workspaceEditorUtils'

/** Interface for TreeDataTransformerParams */
interface TreeDataTransformerParams {
    /** Array of tree nodes */
    treeData: DataNode[]
    /** Object containing CSS classes used for styling the TreeView */
    classes: ReturnType<typeof useTreeViewStyles>
    /** Optional tree reference for virtualization support */
    treeRef?: React.MutableRefObject<{
        scrollTo?: (options: {
            key: string | number
            align?: 'top' | 'bottom' | 'auto'
            offset?: number
        }) => void
    }>
    /** Project ID for header node */
    projectId?: string
    /** Selected project element node for header */
    selectedProjectElementNode?: ProjectElementNode
    /** Loading state for header */
    isLoading?: boolean
}

/**
 * Type of Transformed node for the tree
 * @param {ReactNode} title - Content Item to render
 * @param {string} className - CSS class used for styling the node
 * @param {ReactNode} switcherIcon - Icon to render for expand/collapse button
 * @param {ReactNode} icon - Hover icon(s) to render for content
 * @param {TransformedNode} children - Children of the node
 * @param {number} depth - The depth of content in hierarchical tree node.
 */
export type TransformedNode = DataNode & {
    title: ReactNode
    className?: string
    switcherIcon?: ReactNode
    icon?: ReactNode
    children?: TransformedNode[]
    depth?: number
    role?: string
    'data-testid'?: string
    'data-key'?: string | React.Key
    'aria-expanded'?: string
    disabled?: boolean
}

/**
 * Data type for manufacturer/BOD pill nodes.
 * Can be either contentItemView (ContentItemViewNodesType) or contentItem (TextContentElement).
 */
type PillData = Partial<ContentItemViewNodesType> | Partial<TextContentElement>

/**
 * Type guard to check if PillData is a ContentItemViewNodesType.
 * ContentItemViewNodesType has properties like isManufacturers, isBOD, dtvParentId.
 */
const isContentItemViewData = (
    data: PillData
): data is Partial<ContentItemViewNodesType> =>
    data != null &&
    ('isManufacturers' in data || 'isBOD' in data || 'dtvParentId' in data)

/** Splits children into manufacturer/BOD content and everything else, preserving order within each group. */
const partitionManufacturerChildren = (
    children: DataNode[]
): [DataNode[], DataNode[]] => [
    children.filter((child) => child.isManufacturers || child.isBOD),
    children.filter((child) => !child.isManufacturers && !child.isBOD),
]

const LazyNodeTitle = withLazyLoad<NodeTitleProps>(() =>
    import('../components/TreeView/NodeTitle').then((module) => ({
        default: module.NodeTitle as ComponentType<any>,
    }))
)

/**
 * Custom hook for transforming tree data into renderable format.
 *
 * Handles duplicate order values from API by:
 * 1. Filtering duplicate nodes using unique keys
 * 2. Creating consistent arrays for numbering calculations
 * 3. Ensuring proper array alignment between ordering and rendering
 *
 * This prevents numbering gaps (1,2,4,6) when API returns duplicate orders (0,1,1,2)
 * by producing consecutive numbering (1,2,3,4) regardless of original order values.
 *
 * @param {TreeDataTransformerParams} - Parameters for the hook
 * @returns {TransformedNode[]} Transformed tree data with proper consecutive numbering
 */
export const useTreeDataTransformer = ({
    treeData,
    classes,
    treeRef,
    projectId,
    selectedProjectElementNode,
    isLoading,
}: TreeDataTransformerParams): TransformedNode[] => {
    const {
        activeTab,
        isContentTeamFromStudioPage,
        expandedKeys,
        contentItemViewNodes,
    } = useShallowSelector((state) => ({
        activeTab: state.activeTab,
        isContentTeamFromStudioPage: state.isContentTeamFromStudioPage,
        expandedKeys: state.expandedKeys,
        contentItemViewNodes: state.contentItemViewNodes,
    }))

    const { isCurrentlyOnOMWorkspace } = useOMWorkspace()
    const hoveredNode = useHoveredNode()

    const {
        visibilitySettingValues: { areNotesVisible, isInactiveContentVisible },
    } = useShallowEditorActionBar((state) => ({
        visibilitySettingValues: state.visibilitySettingValues,
    }))

    // Calculate import type info once at hook level
    const importInfo = useMemo(
        () => getImportTypeInfo(selectedProjectElementNode),
        [selectedProjectElementNode]
    )
    const enableAddCustomManufacturerInBetaWorkspace =
        processEnv.REACT_APP_EnableAddCustomManufacturerInBetaWorkspace === '1'
    const isProductFamilySelected =
        selectedProjectElementNode?.elementType ===
        ProjectElementType.PRODUCTFAMILY

    // Performance-optimized caches with LRU eviction
    const classCache = useRef(new LRUCache<string, string>(5000))
    const orderLabelCache = useRef(new LRUCache<string, string>(5000))
    const lastTreeDataRef = useRef<DataNode[]>([])

    // Clear caches when tree data changes significantly
    const clearCaches = useCallback(() => {
        classCache.current.clear()
        orderLabelCache.current.clear()
    }, [])

    // Check if tree data has changed significantly
    const hasTreeDataChanged = useMemo(() => {
        const currentKeys = new Set(treeData.map((node) => node.key))
        const previousKeys = new Set(
            lastTreeDataRef.current.map((node) => node.key)
        )

        if (currentKeys.size !== previousKeys.size) {
            lastTreeDataRef.current = treeData
            return true
        }

        for (const key of currentKeys) {
            if (!previousKeys.has(key)) {
                lastTreeDataRef.current = treeData
                return true
            }
        }

        lastTreeDataRef.current = treeData
        return false
    }, [treeData])

    // Clear caches when tree structure changes
    React.useEffect(() => {
        if (hasTreeDataChanged) {
            clearCaches()
        }
    }, [hasTreeDataChanged, clearCaches])

    const isHovered = (node: DataNode) =>
        (hoveredNode as DataNode)?.key === node.key
    const nodeClass = useCallback(
        (node: DataNode) => {
            const isNodeHovered = isHovered(node)

            // Use separate cache keys for hovered and non-hovered states
            const baseKey = `${node.key}_${node.type}`
            const hoveredKey = `${baseKey}_hovered`

            // Check cache first - use peek() to avoid expensive LRU reordering on hover
            if (isNodeHovered) {
                const cached = classCache.current.peek(hoveredKey)
                if (cached !== undefined) return cached
            } else {
                const cached = classCache.current.peek(baseKey)
                if (cached !== undefined) return cached
            }

            // Compute class name only if not cached
            const className = clsx(
                node.type === NODE_TYPES.Article && classes.articleContainer,
                isNodeHovered && 'hovered'
            )

            // Store in appropriate cache key - use set() for proper LRU management
            const cacheKey = isNodeHovered ? hoveredKey : baseKey
            classCache.current.set(cacheKey, className)
            return className
        },
        [classes.articleContainer, isHovered]
    )

    // Memoized order label calculation
    const getOrderLabel = useCallback(
        (node: DataNode, visibleNodes: DataNode[], depth: number) => {
            // Add node index to cache key to invalidate cache when position changes during drag/drop or move operations
            const nodeIndex = visibleNodes.findIndex((n) => n.key === node.key)
            const cacheKey = `${node.key}_${visibleNodes.length}_${depth}_${activeTab}_${nodeIndex}`

            // Use peek() for read-heavy operations to avoid LRU reordering overhead
            const cachedValue = orderLabelCache.current.peek(cacheKey)
            if (cachedValue !== undefined) {
                return cachedValue
            }

            const orderLabel = getNodeOrderLabel(
                node,
                visibleNodes,
                depth,
                activeTab,
                contentItemViewNodes
            )

            // Use set() only when adding new entries for proper LRU management
            orderLabelCache.current.set(cacheKey, orderLabel)
            return orderLabel
        },
        [activeTab, contentItemViewNodes]
    )

    // Filter out manufacturers that are not selected
    const checkPillSelected = (contentItemId: string | undefined): boolean => {
        const parentNode =
            findNodeById(contentItemViewNodes, contentItemId ?? '') || null
        return parentNode?.isPillSelected === true
    }

    const isManufacturerVisible = useCallback(
        (node: DataNode) => {
            // Only process manufacturer/BOD nodes
            if (!node.isManufacturers && !node.isBOD) {
                return true // Show all non-manufacturer/BOD nodes
            }

            // Check if it's a manufacturer pill (excluding BOD pills)
            const contentItem = node.contentItemNodes?.[0]
            const pillData: PillData | undefined =
                node.contentItemView || contentItem

            // Identify pill type by classification
            // Use type guard to safely access classifyNode (only works with ContentItemViewNodesType)
            const nodeClass =
                pillData && isContentItemViewData(pillData)
                    ? classifyNode(pillData)
                    : NodeClassification.OTHER

            // If a pill's parent paragraph is inactive (hidden), hide the pill entirely —
            // don't show it with strikethrough; the paragraph's inactivation takes full control.
            // This applies symmetrically to both manufacturer pills and BOD pills.
            if (pillData) {
                const PILL_TO_PARAGRAPH_MAP: Record<string, string> = {
                    [NodeClassification.MANUFACTURER_PILL]:
                        NodeClassification.MANUFACTURER_PARAGRAPH,
                    [NodeClassification.BOD_PILL]:
                        NodeClassification.BOD_PARAGRAPH,
                }
                const paragraphClass = PILL_TO_PARAGRAPH_MAP[nodeClass] ?? null

                if (paragraphClass !== null) {
                    const pillDtvParentId =
                        'dtvParentId' in pillData
                            ? pillData.dtvParentId
                            : undefined
                    const parentParagraph = contentItemViewNodes.find(
                        (n) =>
                            classifyNode(n) === paragraphClass &&
                            n.dtvParentId === pillDtvParentId
                    )
                    if (parentParagraph?.isHidden === true) {
                        return false
                    }
                }
            }

            // Always show synthetic button nodes (product-library-btn, om-add-mfr-btn).
            // These are never real content items and must never be filtered by pill logic.
            const nodeKey = String(node.key)
            if (isSyntheticButtonKey(nodeKey)) {
                return true
            }

            // Use contentItemViewNodes for fresh isHidden — node.isHidden from treeData can be
            // stale for up to 100ms (setTreeData is deferred via scheduleIdleCallback with 100ms timeout).
            const currentIsHidden =
                contentItemViewNodes.find((n) => n.id === node.key)?.isHidden ??
                node.isHidden
            if (currentIsHidden) {
                return true
            }

            // Check if it's a header using centralized utility
            if (isManufacturerOrBODHeader(node)) {
                return true // Always show headers
            }

            // Show manufacturer nodes that have children (paragraphs)
            // These are container nodes that should display the Product Library button
            if (
                node.isManufacturers &&
                node.children &&
                node.children.length > 0
            ) {
                return true
            }

            // Show BOD nodes that have children (container nodes)
            // These are sections containing BOD pills that should always be displayed
            if (node.isBOD && node.children && node.children.length > 0) {
                return true
            }

            // Manufacturer/BOD pills visibility rules:
            // This code path is ONLY executed for base content (not imports or clones).
            // All pills in imports/clones pass through without filtering via shouldSkipFiltering.
            // 1. Custom manufacturers are always shown (manually added by user)
            // 2. Otherwise, filter by isPillSelected (Product Library selection)
            if (node.isCustomManufacturer) {
                return true // Always show custom manufacturers
            }

            return checkPillSelected(contentItem?.id)
        },
        [contentItemViewNodes]
    )

    // Append Product Library Button Node after the Manufacturers
    const getChildrenWithProductLibraryButton = useCallback(
        (node: DataNode) => {
            // Only process manufacturer/BOD container paragraphs. Pills carry a non-null
            // dtvContentItemParentId pointing at their paragraph; synthetic button nodes
            // (product-library-btn, om-add-mfr-btn) are recognized by key prefix. Both must
            // be excluded — otherwise pills get buttons and synthetic buttons recurse.
            // Empty children are allowed for paragraphs so the button still appears on old
            // projects with no manufacturer pills (Bug #2657809).
            const nodeKey = String(node.key)
            const isPillOrSyntheticButton =
                node.contentItemView?.dtvContentItemParentId != null ||
                isSyntheticButtonKey(nodeKey)
            if (
                (!node.isManufacturers && !node.isBOD) ||
                !node.children ||
                isPillOrSyntheticButton
            ) {
                return node.children || []
            }

            // Check if feature flag is enabled
            // Skip for content team view UNLESS on OM workspace (OM needs the add-manufacturer button node)
            if (
                processEnv.REACT_APP_EnableDynamicManufacturerListSelection !==
                    '1' ||
                (isContentTeamFromStudioPage && !isCurrentlyOnOMWorkspace)
            ) {
                return node.children || []
            }

            const children = [...node.children]

            // Separate regular manufacturers from custom manufacturers
            const regularChildren = children.filter(
                (child) =>
                    !child.isCustomManufacturer ||
                    (child.isCustomManufacturer &&
                        child?.contentItemView
                            ?.isAddedInOfficeMasterDraftProject)
            )
            const customManufacturers = children.filter(
                (child) =>
                    child.isCustomManufacturer &&
                    !child?.contentItemView?.isAddedInOfficeMasterDraftProject
            )

            if (isCurrentlyOnOMWorkspace) {
                // On OM workspace, suppress the add-manufacturer row when a Product Family element is selected

                if (isProductFamilySelected) {
                    return children
                }

                // OM workspace: keep all pills (including custom) as regular children,
                // reorder non-manufacturer content after them, then append the button.
                const omBtnKey = `om-add-mfr-btn-${node.key}`
                const existingOmBtn = children.find(
                    (child) => child.key === omBtnKey
                )
                const omChildren = existingOmBtn
                    ? children.filter((child) => child.key !== omBtnKey)
                    : children
                const [
                    omManufacturerChildren,
                    omNonManufacturerChildren,
                ] = partitionManufacturerChildren(omChildren)
                const reorderedOmChildren = [
                    ...omManufacturerChildren,
                    ...omNonManufacturerChildren,
                ]

                if (existingOmBtn)
                    return [...reorderedOmChildren, existingOmBtn]
                if (node?.isHidden) return reorderedOmChildren

                const omAddMfrButton: DataNode = {
                    order: children.length,
                    children: [],
                    key: omBtnKey,
                    contentItemNodes: [],
                    type: 'Paragraph',
                    // isBOD drives button styling/behavior in NodeTitle (renders BOD-specific UI)
                    isBOD: node.isBOD,
                    // Inherited so AddManufacturerButton can refetch its parent
                    // paragraph's content after inserting a custom manufacturer
                    // (reconciles the optimistic pillId with the server's real one).
                    rootArticleId: node.rootArticleId,
                }

                return [...reorderedOmChildren, omAddMfrButton]
            }

            // AE workspace: product-library-btn + custom manufacturers in input fields
            const buttonKey = `product-library-btn-${node.key}`
            const hasButton = children.some((child) => child.key === buttonKey)
            if (hasButton) return children

            // Non-manufacturer content (e.g. a demoted paragraph) must always render
            // after manufacturer/BOD content, never interleaved by its own order value.
            const [manufacturerRegularChildren, nonManufacturerChildren] =
                partitionManufacturerChildren(regularChildren)

            const productLibraryButton: DataNode = {
                order: regularChildren.length,
                children: [],
                key: buttonKey,
                contentItemNodes: [],
                type: 'Paragraph',
                parentProductTypeId:
                    node?.contentItemView?.dtvParentId ??
                    node?.contentItemView?.dtvContentItemParentId,
                rootArticleId: node?.rootArticleId,
                // Do NOT propagate parent paragraph's isHidden to the button's own
                // isHidden — the button must always be visible and interactive (Bug 2688614).
                // parentIsHidden carries the parent's state so CustomManufacturerFields
                // can still be disabled when the paragraph is explicitly deactivated
                // (Bug 2684633/2684545), while the tree-level disabled/filtering is unaffected.
                parentIsHidden: node.isHidden,
                // isBOD drives button styling/behavior in NodeTitle (renders BOD-specific UI)
                isBOD: node.isBOD,
                // Bug 2690689: tag the row with the owning paragraph's id so the
                // input-field wrapper can filter custom-mfr pills to this paragraph
                // only, instead of every custom-mfr pill in the global slice.
                parentContentItemId: node.key,
            }

            // Beta workspace: custom manufacturers render in the input-field
            // wrapper, not the tree — so emit only regular children + button.
            // (OM workspace already returned above, so `isCurrentlyOnOMWorkspace`
            // is false on this path.)
            if (enableAddCustomManufacturerInBetaWorkspace) {
                return [
                    ...manufacturerRegularChildren,
                    productLibraryButton,
                    ...nonManufacturerChildren,
                ]
            }

            return importInfo.isMasterSpecClone
                ? [
                      ...manufacturerRegularChildren,
                      ...customManufacturers,
                      productLibraryButton,
                      ...nonManufacturerChildren,
                  ]
                : [
                      ...manufacturerRegularChildren,
                      productLibraryButton,
                      ...customManufacturers,
                      ...nonManufacturerChildren,
                  ]
        },
        [
            isContentTeamFromStudioPage,
            isCurrentlyOnOMWorkspace,
            importInfo,
            isProductFamilySelected,
            enableAddCustomManufacturerInBetaWorkspace,
        ]
    )
    const isDraging = useIsDragging()
    return useMemo(() => {
        /**
         * Set to track unique node keys across the entire tree traversal.
         * This prevents duplicate nodes from being included in the transformed output,
         * which could cause numbering inconsistencies when API returns duplicate order values.
         */
        const nodeKeys = new Set<string>()

        /**
         * Recursively transforms tree nodes with proper duplicate handling and consecutive numbering.
         *
         * The transformation process:
         * 1. Filter duplicate nodes using unique keys (prevents race conditions)
         * 2. Create separate arrays for numbering calculation vs. display
         * 3. Use the same visibleNodesForOrdering array for all numbering calculations
         *
         * This ensures that nodes with duplicate API order values (0,1,1,2)
         * produce consecutive display numbering (1,2,3,4) instead of gaps (1,2,4,6).
         */
        const transform = (
            nodes: DataNode[],
            depth: number = 0
        ): TransformedNode[] => {
            if (!nodes || nodes.length === 0) {
                return []
            }

            // Step 1: Filter out duplicate nodes using utility function
            const uniqueAllNodes = filterUniqueNodes(nodes, nodeKeys)

            // Step 2: Get filtered arrays ensuring alignment between numbering and display
            const {
                visibleNodesForOrdering,
                nodesToDisplay,
            } = getFilteredNodeArrays(uniqueAllNodes, isInactiveContentVisible)

            // Skip pill filtering entirely for imported families and OM Clones:
            // all manufacturer and BOD pills are always shown as-is.
            // For base content, isManufacturerVisible applies isPillSelected filtering.
            const shouldSkipFiltering =
                importInfo.isProjectImport ||
                importInfo.isOMImport ||
                importInfo.isMasterSpecClone

            const filteredNodesToDisplay =
                processEnv.REACT_APP_EnableDynamicManufacturerListSelection ===
                    '1' &&
                !isContentTeamFromStudioPage &&
                !shouldSkipFiltering
                    ? nodesToDisplay.filter(isManufacturerVisible)
                    : nodesToDisplay

            const isPTNotAdded =
                selectedProjectElementNode?.isInProject === false
            const shouldSuppressDisabled =
                !isCurrentlyOnOMWorkspace && isPTNotAdded

            return filteredNodesToDisplay.map((node) => {
                // Appended ProductLibraryButton after the Manufacturers
                const childrenWithButton = getChildrenWithProductLibraryButton(
                    node
                )

                /**
                 * Generate order label using the consistent visibleNodesForOrdering array.
                 * This array is used for ALL numbering calculations to ensure:
                 * - No gaps in numbering when duplicates are filtered
                 * - Consistent indexing across visible and hidden nodes
                 * - Proper consecutive numbering (1,2,3,4) regardless of API order values
                 */
                const orderLabel = getOrderLabel(
                    node,
                    visibleNodesForOrdering, // Always use same array for consistency
                    depth
                )

                return {
                    ...node,
                    role: 'treeitem',
                    'data-testid': 'tree-child',
                    'data-key': node.key,
                    'aria-expanded': node.isLeaf ? 'false' : 'true',
                    className: nodeClass(node),

                    title: (
                        <LazyNodeTitle
                            node={node}
                            isExpanded={expandedKeys?.includes(node.key)}
                            showNodeOrder={true}
                            orderLabel={orderLabel}
                            classes={classes}
                            areNotesVisible={areNotesVisible}
                            treeRef={treeRef}
                        />
                    ),
                    disabled: isDraging
                        ? false
                        : isContentTeamFromStudioPage
                        ? false
                        : node.isHidden && !shouldSuppressDisabled,
                    depth,
                    children: transform(childrenWithButton || [], depth + 1), // Increment depth for children
                    ...(isSyntheticButtonKey(String(node.key))
                        ? { draggable: false }
                        : {}),
                }
            })
        }
        /**
         * Create header node with WorkspaceTitle and ProjectProperties.
         * This special node is always rendered first (order: -1) and scrolls with tree content.
         * It's disabled to prevent interaction and uses a unique key to identify it.
         */
        const headerNode: TransformedNode = {
            key: '__header_node__',
            type: 'Paragraph', // Use existing type instead of creating new 'Header' type
            contentItemNodes: [],
            order: -1,
            isLeaf: false,
            disabled: !isDraging,
            className: 'tree-header-node',
            title: (
                <div
                    data-testid="tree-header-node"
                    style={{
                        position: 'relative',
                        width: '100%',
                    }}
                >
                    <WorkspaceTitle projectId={projectId} />
                    <ProjectProperties
                        projectElementNode={selectedProjectElementNode}
                        projectId={projectId ?? ''}
                        isLoading={isLoading ?? false}
                    />
                </div>
            ),
        }

        // Prepend header node to transformed tree data
        return [headerNode, ...transform(treeData)]
    }, [
        treeData,
        activeTab,
        expandedKeys,
        classes,
        isInactiveContentVisible,
        areNotesVisible,
        isContentTeamFromStudioPage,
        projectId,
        selectedProjectElementNode,
        isCurrentlyOnOMWorkspace,
        isLoading,
        isDraging,
        isManufacturerVisible,
        getChildrenWithProductLibraryButton,
        nodeClass,
        getOrderLabel,
    ])
}
